/*
 * Turning sky positions into pixels: the camera, the perspective projection, and
 * the brightness/colour model that decides what a star actually looks like.
 *
 * Vectors here are (east, north, up) — right-handed, so east x north = up.
 *
 * The camera is a yaw/pitch pair rather than a free quaternion, and that is
 * deliberate: the horizon must stay level. A quaternion camera would let the sky
 * roll, which no stargazer ever sees and which would wreck the one measurement
 * the game is built on — reading a star's height above the horizon. Pitch is
 * clamped just short of the zenith so the basis never degenerates; at a 60 deg
 * field of view the point straight overhead is still comfortably in frame.
 */

import { DEFAULT_PRESET, makeCurve } from './brightness.mjs';

const DEG = Math.PI / 180;

export const MAX_PITCH_DEG = 88;
export const MIN_PITCH_DEG = -25;

export function altAzToVector(altDeg, azDeg) {
  const cosAlt = Math.cos(altDeg * DEG);
  return {
    east: cosAlt * Math.sin(azDeg * DEG),
    north: cosAlt * Math.cos(azDeg * DEG),
    up: Math.sin(altDeg * DEG)
  };
}

export function makeCamera({ azDeg, altDeg, width, height, fovDeg }) {
  const forward = altAzToVector(altDeg, azDeg);

  // forward x worldUp, with worldUp = (0, 0, 1).
  const rx = forward.north;
  const ry = -forward.east;
  const rLen = Math.hypot(rx, ry) || 1;
  const right = { east: rx / rLen, north: ry / rLen, up: 0 };

  const up = {
    east: right.north * forward.up - right.up * forward.north,
    north: right.up * forward.east - right.east * forward.up,
    up: right.east * forward.north - right.north * forward.east
  };

  return {
    forward,
    right,
    up,
    focal: width / 2 / Math.tan((fovDeg / 2) * DEG),
    centerX: width / 2,
    centerY: height / 2,
    width,
    height
  };
}

const dot = (v, b) => v.east * b.east + v.north * b.north + v.up * b.up;

export function projectVector(vec, camera) {
  const depth = dot(vec, camera.forward);
  if (depth <= 0.01) return null;

  const x = camera.centerX + (camera.focal * dot(vec, camera.right)) / depth;
  const y = camera.centerY - (camera.focal * dot(vec, camera.up)) / depth;

  return { x, y, depth };
}


/*
 * Apparent brightness relative to the brightest star in the sky.
 *
 * The magnitude scale is logarithmic and inverted: five magnitudes is a factor
 * of exactly 100 in flux, and smaller means brighter. Sirius at -1.46 is close
 * to a thousand times brighter than the magnitude 6 stars at the edge of
 * naked-eye visibility. Honouring that ratio is most of what makes a rendered
 * sky look real rather than like scattered confetti.
 */
const DEFAULT_CURVE = makeCurve(DEFAULT_PRESET);

export function relativeFlux(mag) {
  return DEFAULT_CURVE.flux(mag);
}

/*
 * Eyes and screens are nowhere near linear in light, so the raw thousand-to-one
 * flux ratio has to be compressed or every star but a dozen renders as black.
 * How much to compress is a judgement rather than a calculation, so the curve
 * itself lives in brightness.mjs, where it can be swapped; these three are the
 * default one, kept here because everything already imports them from here.
 */
export function brightnessForMagnitude(mag) {
  return DEFAULT_CURVE.brightness(mag);
}

/*
 * How much daylight is in the sky, from the Sun's altitude: 0 in astronomical
 * night, 1 once the stars are gone.
 *
 * Stars do not wait for sunrise to disappear. The sky brightens through
 * twilight and washes them out well before the Sun clears the horizon — by
 * civil twilight, six degrees down, only the brightest are left. So the ramp
 * runs from -18 degrees, where the game considers it properly dark, to -3,
 * rather than to zero.
 *
 * The game itself never sees a value above 0: rounds are generated only in
 * astronomical night. This exists for the tutorial's explorer, where the
 * observer can be dragged into the lit half of the world and ought to watch the
 * sky go out.
 */
export function daylightFactor(sunAltDeg) {
  const t = (sunAltDeg + 18) / 15;
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}

/*
 * Atmospheric extinction. Airmass is the path length through the atmosphere
 * relative to straight up, ~1/sin(altitude). This is why the horizon looks empty
 * in reality, and rendering it makes altitude legible as depth rather than as a
 * hard cutoff at the horizon line.
 *
 * The textbook V-band coefficient is about 0.28 magnitudes per airmass at a good
 * site. This runs well below that, and deliberately: at 0.28 the bottom of the
 * frame went so dark that the lower sky read as missing rather than as hazy. It
 * came down to 0.18 for that, and then again to here — the effect is meant to
 * give the horizon depth, not to empty it, and the lower sky is also where a
 * player is doing the most work, since altitude above the horizon is the whole
 * latitude measurement.
 *
 * The curve is exponential in airmass, so the reduction lands almost entirely
 * where it was wanted: a star five degrees up keeps about 68% more of its light
 * than it did, ten degrees up about 27% more, and by forty-five it is a couple
 * of per cent. Overhead is unchanged by definition.
 */
const EXTINCTION_PER_AIRMASS = 0.126;

export function extinctionFactor(altDeg) {
  if (altDeg <= 0) return 0;
  const airmass = Math.min(1 / Math.sin(Math.max(altDeg, 2) * DEG), 20);
  return 10 ** (-0.4 * EXTINCTION_PER_AIRMASS * (airmass - 1));
}

// Approximate sRGB for each spectral class, hot (blue) to cool (red). Stars
// without a classification in the catalog fall through to white.
const SPECTRAL_COLORS = {
  O: [155, 176, 255],
  B: [170, 191, 255],
  A: [202, 215, 255],
  F: [248, 247, 255],
  G: [255, 244, 234],
  K: [255, 210, 161],
  M: [255, 164, 108]
};

export function starColor(spectralClass) {
  return SPECTRAL_COLORS[spectralClass] || [255, 255, 255];
}

/*
 * The outline of the Moon's lit portion, lit toward +x, for the caller to rotate
 * so the bright limb faces the Sun.
 *
 * Out along the lit limb, then back along the terminator. The terminator is half
 * an ellipse whose half-width is R*(1-2k) measured *against* the lit side: at
 * full it swings right around to the far limb and closes the whole disc, at
 * half-lit it collapses to a straight edge, and at a crescent it cuts back under
 * the limb. Getting that sign backwards traces the return path over the outbound
 * one and encloses nothing, which renders as an unlit disc at every phase.
 */
export function moonLitPath(illuminated, radius, steps = 48) {
  const terminator = radius * (1 - 2 * illuminated);
  const points = [];

  for (let i = 0; i <= steps; i += 1) {
    const theta = -Math.PI / 2 + (Math.PI * i) / steps;
    points.push([radius * Math.cos(theta), radius * Math.sin(theta)]);
  }
  for (let i = steps; i >= 0; i -= 1) {
    const theta = -Math.PI / 2 + (Math.PI * i) / steps;
    points.push([terminator * Math.cos(theta), radius * Math.sin(theta)]);
  }

  return points;
}

/*
 * Screen-space angle from the Moon toward the Sun, which is where the bright
 * limb points. Taken as the component of the Sun's direction perpendicular to
 * the Moon's, then projected, so it stays correct with the Sun below the
 * horizon or behind the camera, which it always is during a round.
 *
 * This is what makes the Moon readable as a clue rather than decoration: the lit
 * edge genuinely aims at the hidden Sun, so stepping the phase angle back from
 * the Moon's position among the stars lands on the Sun's, and that is a date.
 */
export function brightLimbAngle(moonVec, sunVec, camera) {
  const d = moonVec.east * sunVec.east + moonVec.north * sunVec.north + moonVec.up * sunVec.up;

  const perp = {
    east: sunVec.east - d * moonVec.east,
    north: sunVec.north - d * moonVec.north,
    up: sunVec.up - d * moonVec.up
  };
  const length = Math.hypot(perp.east, perp.north, perp.up);
  if (length < 1e-9) return 0;

  const step = 0.02;
  const nudged = {
    east: moonVec.east + (perp.east / length) * step,
    north: moonVec.north + (perp.north / length) * step,
    up: moonVec.up + (perp.up / length) * step
  };

  const here = projectVector(moonVec, camera);
  const toward = projectVector(nudged, camera);
  if (!here || !toward) return 0;

  return Math.atan2(toward.y - here.y, toward.x - here.x);
}

export function starRadius(brightness) {
  return DEFAULT_CURVE.radius(brightness);
}

// Deliberately reaches 1 well before the very brightest star, so the top of the
// range renders as genuinely white rather than grey.
export function starAlpha(brightness) {
  return DEFAULT_CURVE.alpha(brightness);
}

/*
 * Compass points, and the nearest one to a bearing. Azimuth is folded first so
 * negative and over-360 values (which panning produces freely) still land.
 */
export const CARDINALS = [
  ['N', 0], ['NE', 45], ['E', 90], ['SE', 135],
  ['S', 180], ['SW', 225], ['W', 270], ['NW', 315]
];

export function bearingName(azimuth) {
  const folded = (((azimuth % 360) + 360) % 360);
  return CARDINALS[Math.round(folded / 45) % 8][0];
}

/*
 * Below this, two labels sit on top of each other and neither is readable. Kept
 * small on purpose, so only genuinely overlapping pairs are thinned and not
 * stars that merely share a patch of sky.
 *
 * The case that motivates it is Alpha Centauri, which the catalogue carries as
 * two entries a fraction of a degree apart, so "Rigil Kentaurus" and "Toliman"
 * print on the same point.
 */
const LABEL_SEPARATION_DEG = 1.5;

function tooClose(a, b) {
  if (a.alt === undefined || b.alt === undefined) return false;
  const D = Math.PI / 180;
  const cos =
    Math.sin(a.alt * D) * Math.sin(b.alt * D) +
    Math.cos(a.alt * D) * Math.cos(b.alt * D) * Math.cos((a.az - b.az) * D);
  return Math.acos(Math.min(1, Math.max(-1, cos))) / D < LABEL_SEPARATION_DEG;
}

/*
 * Which stars carry a name on the reveal, chosen from everything above the
 * horizon and deliberately not from what is on screen.
 *
 * Ranking only the visible ones made labels flicker while panning: a mid-bright
 * star like Rasalhague holds a slot in an empty patch of sky, loses it the moment
 * Vega drifts into frame, and never gets it back. Deciding once per round means a
 * name belongs to a star and panning only moves it.
 *
 * Working brightest-first means that when two stars are too close to label
 * separately, the brighter one is already placed and the dimmer is the one
 * dropped. Polaris is always included whatever its rank, since finding it is the
 * game.
 */
export function chooseStarLabels(stars, limit) {
  const named = stars.filter((star) => star.name);
  named.sort((a, b) => a.mag - b.mag);

  const chosen = [];
  for (const star of named) {
    if (chosen.length >= limit) break;
    if (chosen.some((placed) => tooClose(placed, star))) continue;
    chosen.push(star);
  }

  const polaris = named.find((star) => star.name === 'Polaris');
  if (polaris && !chosen.includes(polaris)) chosen.push(polaris);
  return chosen;
}
